import React from 'react';
import { Link, useLocation } from 'react-router-dom';
import { useApp } from '../context/AppContext';
import { useAuth } from '../context/AuthContext';
import {
  Home,
  Users,
  Network,
  Upload,
  Tag,
  Menu,
  X,
  LogOut,
  RefreshCw,
  Calendar
} from 'lucide-react';

function Layout({ children }) {
  const [sidebarOpen, setSidebarOpen] = React.useState(false);
  const location = useLocation();
  const { user } = useApp();
  const { logout } = useAuth();

  const navItems = [
    { path: '/', label: 'Dashboard', icon: Home },
    { path: '/contacts', label: 'Contacts', icon: Users },
    { path: '/network', label: 'Network', icon: Network },
    { path: '/events', label: 'Events', icon: Calendar },
    { path: '/tags', label: 'Tags', icon: Tag },
    { path: '/import', label: 'Import', icon: Upload },
  ];

  const isActive = (path) => {
    if (path === '/') return location.pathname === '/';
    return location.pathname.startsWith(path);
  };

  const handleLogout = async () => {
    try {
      await logout();
    } catch (err) {
      console.error('Logout error:', err);
    }
  };

  return (
    <div style={{ display: 'flex', minHeight: '100vh' }}>
      {/* Mobile sidebar overlay */}
      {sidebarOpen && (
        <div
          style={{
            position: 'fixed',
            inset: 0,
            background: 'rgba(0,0,0,0.5)',
            zIndex: 40
          }}
          onClick={() => setSidebarOpen(false)}
        />
      )}

      {/* Sidebar */}
      <aside
        style={{
          width: '256px',
          background: 'white',
          borderRight: '1px solid var(--gray-200)',
          position: 'fixed',
          top: 0,
          bottom: 0,
          left: sidebarOpen ? 0 : '-256px',
          zIndex: 50,
          transition: 'left 0.3s',
          display: 'flex',
          flexDirection: 'column'
        }}
        className="sidebar"
      >
        <div style={{
          padding: '1.5rem',
          borderBottom: '1px solid var(--gray-200)',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'space-between'
        }}>
          <Link to="/" style={{
            fontSize: '1.25rem',
            fontWeight: '700',
            color: 'var(--primary)',
            textDecoration: 'none',
            display: 'flex',
            alignItems: 'center',
            gap: '0.5rem'
          }}>
            <Network size={24} />
            Network Manager
          </Link>
          <button
            className="btn btn-ghost mobile-only"
            onClick={() => setSidebarOpen(false)}
            style={{ padding: '0.5rem' }}
          >
            <X size={20} />
          </button>
        </div>

        <nav style={{ padding: '1rem', flex: 1 }}>
          {navItems.map(item => {
            const Icon = item.icon;
            const active = isActive(item.path);
            return (
              <Link
                key={item.path}
                to={item.path}
                onClick={() => setSidebarOpen(false)}
                style={{
                  display: 'flex',
                  alignItems: 'center',
                  gap: '0.75rem',
                  padding: '0.75rem 1rem',
                  borderRadius: '0.5rem',
                  marginBottom: '0.25rem',
                  color: active ? 'var(--primary)' : 'var(--gray-600)',
                  background: active ? 'rgba(30, 58, 95, 0.1)' : 'transparent',
                  textDecoration: 'none',
                  fontWeight: active ? '500' : '400',
                  transition: 'all 0.2s'
                }}
              >
                <Icon size={20} />
                {item.label}
              </Link>
            );
          })}
        </nav>

        {/* User section at bottom of sidebar */}
        <div style={{
          padding: '1rem',
          borderTop: '1px solid var(--gray-200)'
        }}>
          {user && (
            <div style={{ marginBottom: '0.75rem' }}>
              <div style={{
                display: 'flex',
                justifyContent: 'space-between',
                alignItems: 'center',
                marginBottom: '0.25rem'
              }}>
                <span style={{
                  fontSize: '0.75rem',
                  color: 'var(--gray-500)'
                }}>
                  Signed in as
                </span>
                <button
                  onClick={handleLogout}
                  style={{
                    background: 'none',
                    border: 'none',
                    color: 'var(--primary)',
                    cursor: 'pointer',
                    fontSize: '0.75rem',
                    fontWeight: '500',
                    display: 'flex',
                    alignItems: 'center',
                    gap: '0.25rem',
                    padding: '0.125rem 0'
                  }}
                  title="Sign in with a different account"
                >
                  <RefreshCw size={12} />
                  Change User
                </button>
              </div>
              <div style={{
                fontSize: '0.875rem',
                fontWeight: '500',
                color: 'var(--gray-700)',
                overflow: 'hidden',
                textOverflow: 'ellipsis',
                whiteSpace: 'nowrap'
              }}>
                {user.email}
              </div>
            </div>
          )}
          <button
            onClick={handleLogout}
            className="btn btn-secondary"
            style={{
              width: '100%',
              justifyContent: 'center'
            }}
          >
            <LogOut size={18} />
            Sign Out
          </button>
        </div>
      </aside>

      {/* Desktop sidebar spacer */}
      <div className="sidebar-spacer" style={{ width: '256px', flexShrink: 0 }} />

      {/* Main content */}
      <main style={{ flex: 1, minWidth: 0 }}>
        {/* Mobile header */}
        <header
          className="mobile-header"
          style={{
            display: 'none',
            alignItems: 'center',
            justifyContent: 'space-between',
            padding: '1rem',
            background: 'white',
            borderBottom: '1px solid var(--gray-200)',
            position: 'sticky',
            top: 0,
            zIndex: 30
          }}
        >
          <button
            className="btn btn-ghost"
            onClick={() => setSidebarOpen(true)}
            style={{ padding: '0.5rem' }}
          >
            <Menu size={24} />
          </button>
          <span style={{ fontWeight: '600', color: 'var(--primary)' }}>
            Network Manager
          </span>
          <div style={{ width: '40px' }} />
        </header>

        <div style={{ padding: '2rem' }}>
          {children}
        </div>
      </main>

      <style>{`
        @media (min-width: 769px) {
          .sidebar {
            left: 0 !important;
          }
          .mobile-only {
            display: none !important;
          }
        }
        @media (max-width: 768px) {
          .sidebar-spacer {
            display: none !important;
          }
          .mobile-header {
            display: flex !important;
          }
          main > div {
            padding: 1rem !important;
          }
        }
      `}</style>
    </div>
  );
}

export default Layout;
